import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";

export default function PersonalPasswordDialog({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const closeEscape = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", closeEscape);
    return () => document.removeEventListener("keydown", closeEscape);
  }, [busy, onClose]);

  const submit = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("تأكيد كلمة المرور غير مطابق");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success("تم تغيير كلمة المرور");
      onClose();
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
      data-testid="personal-password-modal"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-xl"
        data-testid="personal-password-form"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-extrabold">تغيير كلمة المرور</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="grid h-9 w-9 place-items-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200"
            aria-label="إغلاق"
            data-testid="personal-password-close"
          >
            <X size={17}/>
          </button>
        </div>
        <div className="space-y-3">
          <PasswordField
            label="كلمة المرور الحالية"
            value={currentPassword}
            onChange={setCurrentPassword}
            testid="personal-password-current"
            autoFocus
          />
          <PasswordField
            label="كلمة المرور الجديدة"
            value={newPassword}
            onChange={setNewPassword}
            testid="personal-password-new"
          />
          <PasswordField
            label="تأكيد كلمة المرور الجديدة"
            value={confirmPassword}
            onChange={setConfirmPassword}
            testid="personal-password-confirm"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full rounded-2xl bg-orange-600 py-3 text-sm font-extrabold text-white hover:bg-orange-700 disabled:opacity-60"
          data-testid="personal-password-submit"
        >
          {busy ? "جاري الحفظ…" : "حفظ كلمة المرور"}
        </button>
      </form>
    </div>
  );
}

function PasswordField({ label, value, onChange, testid, autoFocus = false }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input
        type="password"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        minLength={6}
        autoFocus={autoFocus}
        required
        className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        data-testid={testid}
      />
    </label>
  );
}